import { Component, Inject, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faFolder, faFile, faArrowUp, faTrash, faFolderPlus, faUpload, faHome, faFileExcel, faFileWord, faFilePowerpoint, faFilePdf, faDownload, faCropSimple } from '@fortawesome/free-solid-svg-icons';
import { BreadCrumbModule } from '@progress/kendo-angular-navigation';
import {
  ApplicationApiService,
  AttachmentDto,
  AttachmentType,
  AttachmentNewFolderRequest,
  AttachmentDeleteFolderRequest,
  FileParameter,
  AttachmentDeleteFileRequest
} from 'src/app/services/application-api.service';
import { SnackBarService } from 'src/app/services/snackbar.service';
import { InfoboxComponent } from 'src/app/components/infobox/infobox.component';

export interface RoadmapAttachmentsDialogData {
  roadmapId: number;
  targetConditionId: number;
  targetConditionName: string;
  attachments: AttachmentDto[];
}

interface BreadcrumbItem {
  text: string;
  path: string;
}

@Component({
  selector: 'app-roadmap-attachments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatProgressBarModule,
    MatMenuModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    FontAwesomeModule,
    BreadCrumbModule
  ],
  templateUrl: './roadmap-attachments.component.html',
  styleUrl: './roadmap-attachments.component.scss'
})
export class RoadmapAttachmentsComponent implements OnInit {
  private api = inject(ApplicationApiService);
  private snackbar = inject(SnackBarService);
  private dialog = inject(MatDialog);

  // Icons
  faFolder = faFolder;
  faFile = faFile;
  faArrowUp = faArrowUp;
  faTrash = faTrash;
  faFolderPlus = faFolderPlus;
  faUpload = faUpload;
  faHome = faHome;
  faFileExcel = faFileExcel;
  faFileWord = faFileWord;
  faFilePowerpoint = faFilePowerpoint;
  faFilePdf = faFilePdf;
  faDownload = faDownload;

  // State
  attachments = signal<AttachmentDto[]>([]);
  currentPath = signal<string>('');
  currentItems = signal<AttachmentDto[]>([]);
  breadcrumbs = signal<BreadcrumbItem[]>([]);
  isLoading = signal<boolean>(false);
  isUploading = signal<boolean>(false);
  selectedItems = signal<Set<string>>(new Set());
  selectedFile = signal<AttachmentDto | null>(null);

  // New folder dialog state
  showNewFolderInput = signal<boolean>(false);
  newFolderName = '';

  // Attachment type enum reference
  readonly AttachmentType = AttachmentType;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RoadmapAttachmentsDialogData,
    public dialogRef: MatDialogRef<RoadmapAttachmentsComponent>
  ) {}

  ngOnInit(): void {
    this.attachments.set(this.data.attachments ?? []);
    this.navigateToPath(this.currentPath());
  }

  /**
   * Gets the children array at the current path from the attachments collection.
   * Returns the root attachments array if at root, otherwise returns the children of the folder at the current path.
   */
  private getAttachmentsAtCurrentPath(): AttachmentDto[] {
    const path = this.currentPath();
    if (!path) {
      return this.attachments();
    }

    const parts = path.split('/').filter(p => p);
    let items = this.attachments();
    for (const part of parts) {
      const folder = items.find(item => item.name === part && item.type === AttachmentType.Folder);
      if (folder && folder.children) {
        items = folder.children;
      } else {
        return [];
      }
    }
    return items;
  }

  navigateToPath(path: string): void {
    this.currentPath.set(path);
    this.selectedItems.set(new Set());
    this.selectedFile.set(null);

    // Build breadcrumbs
    const parts = path.split('/').filter(p => p);
    const breadcrumbs: BreadcrumbItem[] = [{ text: 'Root', path: '' }];
    let currentBreadcrumbPath = '';
    for (const part of parts) {
      currentBreadcrumbPath += (currentBreadcrumbPath ? '/' : '') + part;
      breadcrumbs.push({ text: part, path: currentBreadcrumbPath });
    }
    this.breadcrumbs.set(breadcrumbs);

    // Get items at current path
    let items = this.attachments();
    for (const part of parts) {
      const folder = items.find(item => item.name === part && item.type === AttachmentType.Folder);
      if (folder && folder.children) {
        items = folder.children;
      } else {
        items = [];
        break;
      }
    }

    // Sort: folders first (alphabetically), then files (alphabetically)
    const sortedItems = [...items].sort((a, b) => {
      const aIsFolder = a.type === AttachmentType.Folder;
      const bIsFolder = b.type === AttachmentType.Folder;

      if (aIsFolder && !bIsFolder) return -1;
      if (!aIsFolder && bIsFolder) return 1;

      return (a.name ?? '').localeCompare(b.name ?? '');
    });

    this.currentItems.set(sortedItems);
  }

  navigateUp(): void {
    const parts = this.currentPath().split('/').filter(p => p);
    parts.pop();
    this.navigateToPath(parts.join('/'));
  }

  onBreadcrumbClick(item: BreadcrumbItem): void {
    this.navigateToPath(item.path);
  }

  onItemClick(item: AttachmentDto): void {
    if (item.type === AttachmentType.Folder) {
      // It's a folder, navigate into it
      const newPath = this.currentPath() ? `${this.currentPath()}/${item.name}` : item.name!;
      this.navigateToPath(newPath);
    } else {
      // It's a file, select it for download
      this.selectedFile.set(item);
      this.toggleSelection(item);
    }
  }

  onItemDoubleClick(item: AttachmentDto): void {
    if (item.type === AttachmentType.Folder) {
      const newPath = this.currentPath() ? `${this.currentPath()}/${item.name}` : item.name!;
      this.navigateToPath(newPath);
    } else {
      // It's a file, download it
      this.downloadFile(item);
    }
  }

  toggleSelection(item: AttachmentDto): void {
    const itemKey = `${this.currentPath()}/${item.name}`;
    const currentSelected = this.selectedItems();

    // If clicking the same item, deselect it
    if (currentSelected.has(itemKey)) {
      this.selectedItems.set(new Set());
      this.selectedFile.set(null);
    } else {
      // Select only this item (single selection)
      this.selectedItems.set(new Set([itemKey]));
    }
  }

  isSelected(item: AttachmentDto): boolean {
    const itemKey = `${this.currentPath()}/${item.name}`;
    return this.selectedItems().has(itemKey);
  }

  // File upload
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const files: FileParameter[] = [];
    for (let i = 0; i < input.files.length; i++) {
      const file = input.files[i];
      files.push({
        data: file,
        fileName: file.name
      });
    }

    this.uploadFiles(files);
    input.value = ''; // Reset input
  }

  uploadFiles(files: FileParameter[]): void {
    this.isUploading.set(true);
    const folderPath = this.currentPath();

    this.api.apiAttachmentsUpload(this.data.roadmapId, this.data.targetConditionId, folderPath, files).subscribe({
      next: () => {
        this.snackbar.success(`${files.length} file(s) uploaded successfully`);
        //push to current attachments and attachments collection
        const attachmentsAtPath = this.getAttachmentsAtCurrentPath();
        for (const file of files) {
          // Check if file already exists in currentItems
          const existingFile = this.currentItems().find(
            item => item.name === file.fileName && item.type === AttachmentType.File
          );
          if (!existingFile) {
            const newFile: AttachmentDto = {
              name: file.fileName,
              type: AttachmentType.File,
              children: []
            };
            this.currentItems().push(newFile);
            attachmentsAtPath.push(newFile);
          }
        }
        this.isUploading.set(false);
      },
      error: () => {
        this.snackbar.error('Failed to upload files');
        this.isUploading.set(false);
      }
    });
  }

  // Create folder
  showNewFolderDialog(): void {
    this.showNewFolderInput.set(true);
    this.newFolderName = '';
  }

  cancelNewFolder(): void {
    this.showNewFolderInput.set(false);
    this.newFolderName = '';
  }

  createFolder(): void {
    if (!this.newFolderName.trim()) {
      this.snackbar.error('Folder name is required');
      return;
    }

    // Check if folder already exists
    const existingFolder = this.currentItems().find(
      item => item.name === this.newFolderName.trim() && item.type === AttachmentType.Folder
    );
    if (existingFolder) {
      this.snackbar.error(`Folder "${this.newFolderName.trim()}" already exists`);
      return;
    }

    const request: AttachmentNewFolderRequest = {
      roadmapId: this.data.roadmapId,
      targetConditionId: this.data.targetConditionId,
      folderPath: this.currentPath(),
      newFolderName: this.newFolderName.trim()
    };

    this.isLoading.set(true);
    this.api.apiAttachmentsFolderPost(request).subscribe({
      next: () => {
        this.snackbar.success(`Folder "${this.newFolderName}" created`);
        this.showNewFolderInput.set(false);
        this.newFolderName = '';
        // create new AttachmentDto for the new folder
        const newFolder: AttachmentDto = {
          name: request.newFolderName,
          type: AttachmentType.Folder,
          children: []
        };
        this.currentItems().push(newFolder);
        // Also add to attachments collection
        const attachmentsAtPath = this.getAttachmentsAtCurrentPath();
        attachmentsAtPath.push(newFolder);
        //sort folders to top
        this.currentItems.set([...this.currentItems()].sort((a, b) => {
          const aIsFolder = a.type === AttachmentType.Folder;
          const bIsFolder = b.type === AttachmentType.Folder;
          if (aIsFolder && !bIsFolder) return -1;
          if (!aIsFolder && bIsFolder) return 1;
          return a.name!.localeCompare(b.name!);
        }));
        this.isLoading.set(false);
      },
      error: () => {
        this.snackbar.error('Failed to create folder');
        this.isLoading.set(false);
      }
    });
  }

  // Delete
  deleteItem(item: AttachmentDto): void {
    const itemPath = this.currentPath() ? `${this.currentPath()}/${item.name}` : item.name!;
    const itemType = item.type === AttachmentType.Folder ? 'folder' : 'file';

    const message = itemType === 'folder'
      ? `Are you sure you want to delete this folder: "${item.name}"?\n\nAll files and subfolders within this folder will also be deleted.`
      : `Are you sure you want to delete this file: "${item.name}"?`;

    const dialogRef = this.dialog.open(InfoboxComponent, {
      data: {
        title: `Delete ${itemType}`,
        content: message
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }

      if (itemType === 'file') {
        //delete file
        const fileDeleteRequest: AttachmentDeleteFileRequest = {
          roadmapId: this.data.roadmapId,
          targetConditionId: this.data.targetConditionId,
          filePath: itemPath
        };

        this.isLoading.set(true);
        this.api.apiAttachmentsFile(fileDeleteRequest).subscribe({
          next: () => {
            this.snackbar.success(`File "${item.name}" deleted`);
            this.currentItems.set(this.currentItems().filter(i => i !== item));
            // Also remove from attachments collection
            const attachmentsAtPath = this.getAttachmentsAtCurrentPath();
            const index = attachmentsAtPath.findIndex(i => i.name === item.name && i.type === AttachmentType.File);
            if (index > -1) {
              attachmentsAtPath.splice(index, 1);
            }
            this.isLoading.set(false);
          },
          error: () => {
            this.snackbar.error(`Failed to delete file`);
            this.isLoading.set(false);
          }
        });
      } else {
        //delete folder
        const request: AttachmentDeleteFolderRequest = {
          roadmapId: this.data.roadmapId,
          targetConditionId: this.data.targetConditionId,
          folderPath: itemPath
        };

        this.isLoading.set(true);
        this.api.apiAttachmentsFolderDelete(request).subscribe({
          next: () => {
            this.snackbar.success(`Folder "${item.name}" deleted`);
            this.currentItems.set(this.currentItems().filter(i => i !== item));
            // Also remove from attachments collection
            const attachmentsAtPath = this.getAttachmentsAtCurrentPath();
            const index = attachmentsAtPath.findIndex(i => i.name === item.name && i.type === AttachmentType.Folder);
            if (index > -1) {
              attachmentsAtPath.splice(index, 1);
            }
            this.isLoading.set(false);
          },
          error: () => {
            this.snackbar.error(`Failed to delete folder`);
            this.isLoading.set(false);
          }
        });
      }
    });
  }

  getItemIcon(item: AttachmentDto) {
    if(item.type === AttachmentType.Folder)
      return this.faFolder
    else {
      const extension = item.name?.split('.').pop()?.toLowerCase();
      switch(extension) {
        case 'xls': case 'xlsx':
          return this.faFileExcel;
        case 'doc': case 'docx':
          return this.faFileWord;
        case 'ppt': case 'pptx':
          return this.faFilePowerpoint;
        case 'pdf':
          return this.faFilePdf;
        default:
          return this.faFile;
      }
    }
  }

  getItemIconClass(item: AttachmentDto): string {
    return item.type === AttachmentType.Folder ? 'icon-folder' : 'icon-file';
  }

  downloadFile(item: AttachmentDto): void {
    const filePath = this.currentPath() ? `${this.currentPath()}/${item.name}` : `${item.name!}`;

    this.api.apiAttachmentsDownload(this.data.roadmapId, this.data.targetConditionId, filePath).subscribe({
      next: (response: any) => {
        // Create blob URL and trigger download
        const blob = response.data as Blob;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = item.name!;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      },
      error: () => {
        this.snackbar.error('Failed to Download File');
      }
    });
  }

  downloadSelectedFile(): void {
    const file = this.selectedFile();
    if (file) {
      this.downloadFile(file);
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
